import { useMemo, useState } from "react";
import { buildInvoiceHtml, printInvoice, type InvoiceFormat, type InvoicePrintData } from "@/lib/invoiceTemplate";
import type { PrinterConfig } from "@/hooks/usePosConfig";

interface InvoicePreviewProps {
  data: InvoicePrintData | null;
  loading: boolean;
  error: string | null;
  defaultFormat?: InvoiceFormat;
  posWidth?: number | null;
  printer?: PrinterConfig | null;
}

export default function InvoicePreview({
  data,
  loading,
  error,
  defaultFormat = "pos80",
  posWidth,
  printer,
}: InvoicePreviewProps) {
  const [format, setFormat] = useState<InvoiceFormat>(
    posWidth === 58 ? "pos58" : defaultFormat,
  );

  const html = useMemo(() => (data ? buildInvoiceHtml(data, format) : ""), [data, format]);

  const handlePrint = () => {
    if (!data) return;
    const isA4 = format === "a4";
    printInvoice(data, format, {
      kind: isA4 ? "a4" : "ticket",
      printer: isA4 ? printer?.a4_printer ?? null : printer?.pos_printer ?? null,
      widthMm: isA4 ? undefined : format === "pos58" ? 58 : 80,
      copies: printer?.copies ?? 1,
      openDrawer: isA4 ? false : printer?.open_drawer ?? false,
    });
  };

  const formats: { key: InvoiceFormat; label: string; icon: string }[] = [
    { key: "pos58", label: "POS 58mm", icon: "ri-printer-line" },
    { key: "pos80", label: "POS 80mm", icon: "ri-printer-line" },
    { key: "a4", label: "A4 / PDF", icon: "ri-file-pdf-2-line" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg"></i>
        Cargando factura...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-error-warning-line text-2xl"></i>
        </span>
        <p className="text-sm text-foreground-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
          {formats.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setFormat(item.key)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                format === item.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className={`${item.icon} text-sm`}></i>
              {item.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handlePrint}
          disabled={!data}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 disabled:opacity-50"
        >
          <i className="ri-printer-line text-base"></i>
          Imprimir
        </button>
      </div>

      <div className="overflow-auto rounded-lg border border-background-200 bg-background-100 p-3">
        <iframe
          title="Vista previa de factura"
          srcDoc={html}
          style={format === "a4" ? undefined : { width: format === "pos58" ? "219px" : "302px" }}
          className={`mx-auto block h-[62vh] bg-white ${format === "a4" ? "w-full" : ""}`}
        />
      </div>
    </div>
  );
}